import React, { FC, ReactElement, useEffect, useMemo } from 'react'
import { PartitionModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
import { useFormContext, UseFormMethods, useWatch } from '../../../Vendor/react-hook-form/dist/index.ie11'
import FormRow from '../../Common/Components/FormRow'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { OutlinedSelect } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelect'
import { ComboBox } from '../../Common/Components/ComboBox/ComboBox'
import { SelectOption } from '../../Common/Components/Selects/Types'
import {
    PARTITION_SYNC_ENVIRONMENT,
    PARTITION_SYNC_OPTION,
    PARTITION_SYNC_OPTIONS,
    PARTITION_SYNC_TYPE,
    PartitionSyncOption
} from '../Constants/Constants'
import { PartitionConfigurationForm } from '../Types/Types'
import { PartitionModalSyncLayout, PartitionModalTelSysLayout } from '../Utils/partitionModalTelSysLayout'
import {
    PARTITION_COMMS_ENABLED_FIELD_NAME,
    PARTITION_FORCE_MONITORED_FIELD_NAME,
    PARTITION_ID_ON_TELSYS_FIELD_NAME,
    PARTITION_ID_ON_TELSYS_OWNER_FIELD_NAME,
    PARTITION_IS_GLOBAL_FIELD_NAME,
    PARTITION_LICENSED_FOR_FIELD_NAME,
    PARTITION_MAINTAIN_PARITY_FIELD_NAME,
    PARTITION_PHOTO_SYNC_NOW_FIELD_NAME,
    PARTITION_SECURITY_GROUP_IDS_FIELD_NAME,
    PARTITION_SYNCHRONISE_SUPERVISOR_PERMISSIONS_FIELD_NAME,
    PARTITION_SYNC_ENVIRONMENT_FIELD_NAME,
    PARTITION_SYNC_NOW_FIELD_NAME,
    PARTITION_SYNC_OPTIONS_FIELD_NAME,
    PARTITION_SYNC_TYPE_FIELD_NAME,
    PARTITION_TELSYS_COMPONENT_TYPE_FIELD_NAME
} from '../Constants/FieldNames'
import {
    getPartitionTelSysComponentTypeOptions,
    PartitionTelSysComponentType,
    shouldShowPartitionTelSysComponentTypeId,
    shouldShowPartitionTelSysComponentTypeOwner
} from '../Utils/partitionTelSysComponentTypes'

const PARTITION_CONFIG_TAB_CLASS = 'partition-config-tab'

type Props = {
    viewOnly?: boolean
    telSysLayout: PartitionModalTelSysLayout
}

/**
 * Determines whether the current licence supports supervisor-permission syncing.
 *
 * @param licensedFor - Current partition licence.
 * @returns True when the legacy page would show the supervisor permissions option.
 */
const supportsSupervisorPermissions = (licensedFor: string): boolean =>
    [
        ReportingAccessLevels.ENTERPRISE,
        ReportingAccessLevels.CALL_CENTRE_AGENT,
        ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
    ].includes(licensedFor as ReportingAccessLevels)

/**
 * Determines whether the given sync type is one of the full-sync variants.
 *
 * @param syncType - Current form sync type value.
 * @returns True when the full-sync-only controls should be shown.
 */
const isFullSyncType = (syncType: string): boolean =>
    syncType === PARTITION_SYNC_TYPE.FULL || syncType === PARTITION_SYNC_TYPE.ONE_TIME_FULL

/**
 * Determines whether the sync-now controls should be enabled from the currently visible identifiers.
 *
 * @param syncLayout - Current TelSys sync layout.
 * @param componentTypeValue - Component type mapping value.
 * @param idOnTelSysOwner - Owner identifier value.
 * @param idOnTelSys - Partition identifier value.
 * @returns True when the current configuration is complete enough to enable sync controls.
 */
const canEnableSyncControls = (
    syncLayout: PartitionModalTelSysLayout['syncLayout'],
    showIdOnTelSysOwner: boolean,
    showIdOnTelSys: boolean,
    idOnTelSysOwner: string,
    idOnTelSys: string
): boolean => {
    if (syncLayout === PartitionModalSyncLayout.NONE) return false

    const requiresIdOnTelSys: boolean = syncLayout === PartitionModalSyncLayout.M6 || showIdOnTelSys
    const hasOwner: boolean = idOnTelSysOwner.trim().length > 0
    const hasIdOnTelSys: boolean = idOnTelSys.trim().length > 0

    return (!showIdOnTelSysOwner || hasOwner) && (!requiresIdOnTelSys || hasIdOnTelSys)
}

const useSyncEnvironmentOptions = (language: CLanguageHandler): SelectOption<number>[] => [
    {
        label: language.get(PartitionModal.PartitionModal_Sync_Environment_None),
        value: PARTITION_SYNC_ENVIRONMENT.NONE
    },
    {
        label: language.get(PartitionModal.PartitionModal_Sync_Environment_Tenant),
        value: PARTITION_SYNC_ENVIRONMENT.TENANT
    },
    {
        label: language.get(PartitionModal.PartitionModal_Sync_Environment_AuthoriserSelect),
        value: PARTITION_SYNC_ENVIRONMENT.AUTHORISER_SELECT
    },
    {
        label: language.get(PartitionModal.PartitionModal_Sync_Environment_SecurityGroups),
        value: PARTITION_SYNC_ENVIRONMENT.SECURITY_GROUPS
    }
]

/**
 * Builds synchronisation type select options. Service pack is BroadWorks-only (legacy servlet parity).
 *
 * @param language - Active language handler.
 * @param syncLayout - Layout derived from the parent TelSys type.
 * @returns Options for the sync type select.
 */
const buildSyncTypeOptions = (
    language: CLanguageHandler,
    syncLayout: PartitionModalSyncLayout
): SelectOption<string>[] => {
    const options: SelectOption<string>[] = [
        {
            label: language.get(PartitionModal.PartitionModal_Sync_SynchronisationType_Soft),
            value: PARTITION_SYNC_TYPE.SOFT
        },
        {
            label: language.get(PartitionModal.PartitionModal_Sync_SynchronisationType_OneTimeFull),
            value: PARTITION_SYNC_TYPE.ONE_TIME_FULL
        },
        {
            label: language.get(PartitionModal.PartitionModal_Sync_SynchronisationType_Full),
            value: PARTITION_SYNC_TYPE.FULL
        }
    ]

    if (syncLayout === PartitionModalSyncLayout.BROADWORKS) {
        options.push({
            label: language.get(PartitionModal.PartitionModal_Sync_SynchronisationType_ServicePack),
            value: PARTITION_SYNC_TYPE.SERVICE_PACK
        })
    }

    return options
}

/**
 * Returns a human-readable label for a sync option value.
 *
 * @param language - Active language handler.
 * @param syncOption - API sync option value.
 * @returns Localised display label.
 */
const getSyncOptionLabel = (language: CLanguageHandler, syncOption: PartitionSyncOption): string => {
    switch (syncOption) {
        case PARTITION_SYNC_OPTION.EXTENSIONS_ENDPOINT:
            return language.get(PartitionModal.PartitionModal_Sync_SyncOptions_Endpoints)
        case PARTITION_SYNC_OPTION.EXTENSIONS_TRUNKING:
            return language.get(PartitionModal.PartitionModal_Sync_SyncOptions_Trunking)
        case PARTITION_SYNC_OPTION.AGENTS:
            return language.get(PartitionModal.PartitionModal_Sync_SyncOptions_Agents)
        case PARTITION_SYNC_OPTION.SUPERVISORS:
            return language.get(PartitionModal.PartitionModal_Sync_SyncOptions_Supervisors)
        default:
            return syncOption
    }
}

/**
 * Synchronisation tab with legacy visibility rules restored for the current TelSys type.
 *
 * @param props - Tab props.
 * @returns Synchronisation tab content.
 */
export const PartitionSyncTab: FC<Props> = ({ viewOnly, telSysLayout }: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    const { control, setValue }: UseFormMethods<PartitionConfigurationForm> =
        useFormContext<PartitionConfigurationForm>()
    const disabled: boolean = viewOnly ?? false
    const syncEnvironment: number = Number(
        useWatch({
            control,
            name: PARTITION_SYNC_ENVIRONMENT_FIELD_NAME,
            defaultValue: PARTITION_SYNC_ENVIRONMENT.NONE
        }) ?? PARTITION_SYNC_ENVIRONMENT.NONE
    )
    const syncType: string = String(
        useWatch({
            control,
            name: PARTITION_SYNC_TYPE_FIELD_NAME,
            defaultValue: PARTITION_SYNC_TYPE.ONE_TIME_FULL
        }) ?? PARTITION_SYNC_TYPE.ONE_TIME_FULL
    )
    const licensedForRptUserType: string = String(
        useWatch({
            control,
            name: PARTITION_LICENSED_FOR_FIELD_NAME,
            defaultValue: ReportingAccessLevels.LITE
        }) ?? ReportingAccessLevels.LITE
    )
    const isGlobal: boolean = !!useWatch({
        control,
        name: PARTITION_IS_GLOBAL_FIELD_NAME,
        defaultValue: false
    })
    const commsEnabled: boolean = !!useWatch({
        control,
        name: PARTITION_COMMS_ENABLED_FIELD_NAME,
        defaultValue: true
    })
    const componentTypeValue: string = String(
        useWatch({
            control,
            name: PARTITION_TELSYS_COMPONENT_TYPE_FIELD_NAME,
            defaultValue: EMPTY_STRING
        }) ?? EMPTY_STRING
    )
    const idOnTelSysOwner: string = String(
        useWatch({
            control,
            name: PARTITION_ID_ON_TELSYS_OWNER_FIELD_NAME,
            defaultValue: EMPTY_STRING
        }) ?? EMPTY_STRING
    )
    const idOnTelSys: string = String(
        useWatch({
            control,
            name: PARTITION_ID_ON_TELSYS_FIELD_NAME,
            defaultValue: EMPTY_STRING
        }) ?? EMPTY_STRING
    )

    const syncEnvironmentOptions: SelectOption<number>[] = useSyncEnvironmentOptions(language)
    const syncTypeOptions: SelectOption<string>[] = useMemo(
        (): SelectOption<string>[] => buildSyncTypeOptions(language, telSysLayout.syncLayout),
        [language, telSysLayout.syncLayout]
    )
    const syncOptionChoices: Array<{ label: string; value: PartitionSyncOption }> = useMemo(
        (): Array<{ label: string; value: PartitionSyncOption }> =>
            PARTITION_SYNC_OPTIONS.map(
                (syncOption: PartitionSyncOption): { label: string; value: PartitionSyncOption } => ({
                    label: getSyncOptionLabel(language, syncOption),
                    value: syncOption
                })
            ),
        [language]
    )
    const telSysComponentTypeOptions: SelectOption<string>[] = useMemo(
        (): SelectOption<string>[] => getPartitionTelSysComponentTypeOptions(language, telSysLayout.syncLayout),
        [language, telSysLayout.syncLayout]
    )
    const fullSyncSelected: boolean = isFullSyncType(syncType)
    const showSecurityGroups: boolean =
        telSysLayout.syncLayout === PartitionModalSyncLayout.TEAMS &&
        syncEnvironment === PARTITION_SYNC_ENVIRONMENT.SECURITY_GROUPS
    const showSupervisorPermissions: boolean =
        telSysLayout.syncLayout === PartitionModalSyncLayout.BROADWORKS ||
        supportsSupervisorPermissions(licensedForRptUserType)
    const showIdOnTelSysOwner: boolean =
        telSysLayout.syncLayout !== PartitionModalSyncLayout.M6 &&
        shouldShowPartitionTelSysComponentTypeOwner(telSysLayout.syncLayout, componentTypeValue)
    const showIdOnTelSys: boolean = shouldShowPartitionTelSysComponentTypeId(
        telSysLayout.syncLayout,
        componentTypeValue
    )
    const syncControlsEnabled: boolean =
        !disabled &&
        commsEnabled &&
        canEnableSyncControls(telSysLayout.syncLayout, showIdOnTelSysOwner, showIdOnTelSys, idOnTelSysOwner, idOnTelSys)
    const selectedSyncOptions: string[] = useWatch({
        control,
        name: PARTITION_SYNC_OPTIONS_FIELD_NAME,
        defaultValue: []
    }) as string[]
    const syncOptionsDefaultOption: { label: string; value: string } = {
        label: language.get(PartitionModal.PartitionModal_Sync_SyncOptions_None),
        value: EMPTY_STRING
    }

    useEffect((): void => {
        const showSyncOptionsCombo: boolean =
            telSysLayout.syncLayout === PartitionModalSyncLayout.BROADWORKS && !isGlobal && fullSyncSelected

        if (!showSyncOptionsCombo || selectedSyncOptions.length > 0) return

        setValue(PARTITION_SYNC_OPTIONS_FIELD_NAME, [...PARTITION_SYNC_OPTIONS])
    }, [fullSyncSelected, isGlobal, selectedSyncOptions, setValue, telSysLayout.syncLayout])

    useEffect((): void => {
        if (telSysLayout.syncLayout === PartitionModalSyncLayout.BROADWORKS) return
        if (syncType !== PARTITION_SYNC_TYPE.SERVICE_PACK) return

        setValue(PARTITION_SYNC_TYPE_FIELD_NAME, PARTITION_SYNC_TYPE.ONE_TIME_FULL)
    }, [setValue, syncType, telSysLayout.syncLayout])

    const broadworksTypeLabel: string = useMemo((): string => {
        if (isGlobal) return language.get(PartitionModal.PartitionModal_Sync_CommonDevicesNoSettings)

        return language.get(PartitionModal.PartitionModal_Sync_TypeOnTelSys)
    }, [isGlobal, language])
    const typeOnTelSysLabel: string =
        telSysLayout.syncLayout === PartitionModalSyncLayout.WEBEX
            ? language.get(PartitionModal.PartitionModal_Sync_WebexEnvironmentType)
            : broadworksTypeLabel
    const typeOnTelSysRequiredMessage: string = `${typeOnTelSysLabel} is required`
    let idOnTelSysLabel: string = language.get(PartitionModal.PartitionModal_Sync_GroupID)

    if (telSysLayout.syncLayout === PartitionModalSyncLayout.M6) {
        idOnTelSysLabel = language.get(PartitionModal.PartitionModal_Sync_IDOnTelephonyServerOid)
    } else if (telSysLayout.syncLayout === PartitionModalSyncLayout.WEBEX) {
        idOnTelSysLabel = language.get(PartitionModal.PartitionModal_Sync_UserInLocation)
    }
    const idOnTelSysOwnerLabel: string = language.get(PartitionModal.PartitionModal_Sync_EnterpriseOrProviderID)

    if (telSysLayout.syncLayout === PartitionModalSyncLayout.NONE || isGlobal) {
        return (
            <div className={PARTITION_CONFIG_TAB_CLASS}>
                <div className='partition-config-sync-empty'>
                    {language.get(PartitionModal.PartitionModal_Sync_NoSettings)}
                </div>
            </div>
        )
    }

    return (
        <div className={PARTITION_CONFIG_TAB_CLASS}>
            {telSysLayout.syncLayout === PartitionModalSyncLayout.M6 ? (
                <>
                    <FormRow>
                        <OutlinedTextInput
                            labelLeft={language.get(PartitionModal.PartitionModal_Sync_NameOnTelephonyServer)}
                            inputName={PARTITION_TELSYS_COMPONENT_TYPE_FIELD_NAME}
                            disabled={disabled}
                        />
                    </FormRow>
                    <FormRow>
                        <ValidationErrors name={PARTITION_ID_ON_TELSYS_FIELD_NAME}>
                            <OutlinedTextInput
                                labelLeft={idOnTelSysLabel}
                                inputName={PARTITION_ID_ON_TELSYS_FIELD_NAME}
                                rules={{ required: `${idOnTelSysLabel} is required` }}
                                disabled={disabled}
                            />
                        </ValidationErrors>
                    </FormRow>
                    <FormRow>
                        <OutlinedTextInput
                            labelLeft={language.get(PartitionModal.PartitionModal_Sync_SynchronisationTypeSoftOnly)}
                            inputName={PARTITION_SYNC_TYPE_FIELD_NAME}
                            disabled
                        />
                    </FormRow>
                </>
            ) : (
                <>
                    {telSysLayout.syncLayout !== PartitionModalSyncLayout.TEAMS ? (
                        <FormRow>
                            <ValidationErrors name={PARTITION_TELSYS_COMPONENT_TYPE_FIELD_NAME}>
                                <OutlinedSelect
                                    name={PARTITION_TELSYS_COMPONENT_TYPE_FIELD_NAME}
                                    label={typeOnTelSysLabel}
                                    options={telSysComponentTypeOptions}
                                    rules={{
                                        required: typeOnTelSysRequiredMessage,
                                        validate: (value: string): string | true =>
                                            value !== PartitionTelSysComponentType.UNKNOWN
                                                ? true
                                                : typeOnTelSysRequiredMessage
                                    }}
                                    disabled={disabled}
                                />
                            </ValidationErrors>
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {showIdOnTelSysOwner ? (
                        <FormRow>
                            <ValidationErrors name={PARTITION_ID_ON_TELSYS_OWNER_FIELD_NAME}>
                                <OutlinedTextInput
                                    labelLeft={idOnTelSysOwnerLabel}
                                    inputName={PARTITION_ID_ON_TELSYS_OWNER_FIELD_NAME}
                                    rules={{ required: `${idOnTelSysOwnerLabel} is required` }}
                                    disabled={disabled}
                                />
                            </ValidationErrors>
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {showIdOnTelSys ? (
                        <FormRow>
                            <ValidationErrors name={PARTITION_ID_ON_TELSYS_FIELD_NAME}>
                                <OutlinedTextInput
                                    labelLeft={idOnTelSysLabel}
                                    inputName={PARTITION_ID_ON_TELSYS_FIELD_NAME}
                                    rules={{ required: `${idOnTelSysLabel} is required` }}
                                    disabled={disabled}
                                />
                            </ValidationErrors>
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {telSysLayout.syncLayout === PartitionModalSyncLayout.TEAMS ? (
                        <FormRow>
                            <OutlinedSelect
                                name={PARTITION_SYNC_ENVIRONMENT_FIELD_NAME}
                                label={language.get(PartitionModal.PartitionModal_Sync_TeamsEnvironmentType)}
                                options={syncEnvironmentOptions}
                                disabled={disabled}
                            />
                        </FormRow>
                    ) : (
                        <FormRow>
                            <OutlinedSelect
                                name={PARTITION_SYNC_TYPE_FIELD_NAME}
                                label={language.get(PartitionModal.PartitionModal_Sync_SynchronisationType)}
                                options={syncTypeOptions}
                                disabled={disabled}
                            />
                        </FormRow>
                    )}
                </>
            )}
            {showSecurityGroups ? (
                <FormRow>
                    <OutlinedTextInput
                        labelLeft={language.get(PartitionModal.PartitionModal_Sync_SecurityGroups)}
                        inputName={PARTITION_SECURITY_GROUP_IDS_FIELD_NAME}
                        disabled={disabled}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {telSysLayout.syncLayout === PartitionModalSyncLayout.BROADWORKS && fullSyncSelected ? (
                <FormRow>
                    <ComboBox
                        name={PARTITION_SYNC_OPTIONS_FIELD_NAME}
                        label={language.get(PartitionModal.PartitionModal_Sync_SyncOptions)}
                        multiple
                        defaultOption={syncOptionsDefaultOption}
                        options={syncOptionChoices}
                        variant='outlined'
                        disabled={disabled}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            <FormRow>
                <RouterCheckbox
                    labelLeft={language.get(PartitionModal.PartitionModal_Sync_MaintainParity)}
                    name={PARTITION_MAINTAIN_PARITY_FIELD_NAME}
                    disabled={disabled}
                />
            </FormRow>
            {telSysLayout.syncLayout === PartitionModalSyncLayout.BROADWORKS && fullSyncSelected ? (
                <FormRow>
                    <RouterCheckbox
                        labelLeft={language.get(PartitionModal.PartitionModal_Sync_ForceMonitored)}
                        name={PARTITION_FORCE_MONITORED_FIELD_NAME}
                        disabled={disabled}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {showSupervisorPermissions ? (
                <FormRow>
                    <RouterCheckbox
                        labelLeft={language.get(PartitionModal.PartitionModal_Sync_SupervisorPermissions)}
                        name={PARTITION_SYNCHRONISE_SUPERVISOR_PERMISSIONS_FIELD_NAME}
                        disabled={disabled}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {telSysLayout.syncLayout === PartitionModalSyncLayout.TEAMS ? (
                <FormRow>
                    <RouterCheckbox
                        labelLeft={language.get(PartitionModal.PartitionModal_Sync_PhotoSyncNow)}
                        name={PARTITION_PHOTO_SYNC_NOW_FIELD_NAME}
                        disabled={!syncControlsEnabled}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            <FormRow>
                <RouterCheckbox
                    labelLeft={language.get(PartitionModal.PartitionModal_Sync_SyncNow)}
                    name={PARTITION_SYNC_NOW_FIELD_NAME}
                    disabled={!syncControlsEnabled}
                />
            </FormRow>
        </div>
    )
}
